import type Permission from '@/modules/current-user/permissions/permissions';

export default {
  en: {
    unknown: 'Unknown',
    view: 'View',
    edit: 'Edit',
    admin: 'Admin',
    lead: 'Lead',
    supplier_view: 'View (supplier only)',
    client_view: 'View (client only)',
    supplier_edit: 'Edit (supplier only)',
    client_edit: 'Edit (client only)',
    federated_admin: 'Admin (federated only)',
    federated_edit: 'Edit (federated only)',
    federated_view: 'View (federated only)',
    permissions: {
      ClientsFrameworkConfigApply: {
        title: 'Set framework & add-on requirements',
        description: 'Users can set framework and add-on requirements for suppliers',
      },
      ClientsFrameworkConfigManage: {
        title: 'Manage framework & add-on defaults',
        description: 'Users can set framework and add-on defaults',
      },
      ClientsApprovalChange: {
        title: 'Change approval statuses',
        description: 'User can update approval statuses.',
      },
      ClientsApprovalSettings: {
        title: 'Change approval settings',
        description: 'User can change the approval settings.',
      },
      ClientsConnectionViewsManage: {
        title: 'Manage connection views',
        description: 'User can make changes to connection views.',
      },
      ClientsConnectionsManage: {
        title: 'Manage connection requests',
        description: 'User can create and respond to connection requests.',
      },
      ClientsConnectionProductsManage: {
        title: 'Manage product connections',
        description: 'User can select and modify product connections with suppliers',
      },
      ClientsConnectionsRemove: {
        title: 'Remove suppliers',
        description: 'User can remove supplier connections.',
      },
      ClientsConnectionsTogglePrivate: {
        title: 'Toggle connection privacy',
        description: 'User can toggle connection privacy.',
      },
      ClientsCustomPropertiesSettings: {
        title: 'Configure custom properties',
        description: 'Set ability to create and change custom properties.',
      },
      ClientsCustomPropertiesApply: {
        title: 'Apply custom properties',
        description: 'User can apply custom properties to suppliers.',
      },
      ClientsDiscussionsSend: {
        title: 'Respond to discussions',
        description: 'User can create and respond to discussions.',
      },
      ClientsFilesDownload: {
        title: 'Preview and download files',
        description: 'User can preview and download files.',
      },
      ClientsInsightsManage: {
        title: 'Manage supplier insights questions',
        description: 'User can manage insight questions.',
      },
      ClientsInsightsView: {
        title: 'View supplier insights',
        description: 'User can view supplier insights.',
      },
      ClientsInvitesManage: {
        title: 'Manage invites',
        description: 'User can send, resend and cancel invites.',
      },
      ClientsInvitesView: {
        title: 'View invites',
        description: 'User can view invites.',
      },
      ClientsLabelsApply: {
        title: 'Apply labels',
        description: 'Users can apply labels to suppliers.',
      },
      ClientsLabelsManage: {
        title: 'Configure labels',
        description: 'User can create and edit labels.',
      },
      ClientsNetworkSettings: {
        title: 'Change network contribution settings',
        description: 'User can edit network contribution settings.',
      },
      ClientsNetworkView: {
        title: 'View network visualisations',
        description: 'User can view network visualisations.',
      },
      ClientsOverridesApply: {
        title: 'Apply overrides',
        description: 'User can apply overrides to suppliers.',
      },
      ClientsPoliciesManage: {
        title: 'Manage policies',
        description: 'User can create and edit policies.',
      },
      ClientsRemediationsManage: {
        title: 'Manage remediations',
        description: 'User can create and respond to remediations.',
      },
      ClientsReportingView: {
        title: 'View reporting',
        description: 'User can view reports.',
      },
      ClientsRisksManage: {
        title: 'Manage risks',
        description: 'User can create and edit risks.',
      },
      ClientsRisksNotesAdd: {
        title: 'Add risk notes',
        description: 'User can add notes to risks.',
      },
      ClientsRisksSettings: {
        title: 'Change risks settings',
        description: 'User can change risk settings.',
      },
      ClientsRisksView: {
        title: 'View risks',
        description: 'User can view risks.',
      },
      ClientsSuppliersNotesManage: {
        title: 'Manage private notes',
        description: 'User can create, edit and delete private notes.',
      },
      ClientsSuppliersNotesView: {
        title: 'View private notes',
        description: 'User can view private notes.',
      },
      ClientsAssignedUsersApply: {
        title: 'Assign users',
        description: 'User can assign users to suppliers.',
      },
      ClientsSuppliersView: {
        title: 'View suppliers',
        description: 'User can view suppliers list and profiles.',
      },
      ClientsSuppliersManage: {
        title: 'Create suppliers',
        description: 'User can create suppliers profiles.',
      },
      ClientsTagsApply: {
        title: 'Apply tags',
        description: 'Users can apply tags to suppliers.',
      },
      ClientsCommunitiesMessageboardView: {
        title: 'View community message board',
        description: 'User can view the feed and post pages in communities.',
      },
      ClientsCommunitiesMessageboardPost: {
        title: 'Post and reply in community message board',
        description: 'User can post in communities and reply to posts.',
      },
      ClientsCommunitiesMessageboardManage: {
        title: 'Manage community message board posts',
        description:
          'User can delete a post or a reply that was made by another user in their organisation.',
      },
      ClientsCommunitiesSignalsView: {
        title: 'View communities signals',
        description: 'User can view community related signals on supplier profiles.',
      },
      ClientsCommunitiesInvitesManage: {
        title: 'Manage community invites',
        description: 'User can manage community invites.',
      },
      ClientsExternalMonitoringManage: {
        title: 'Manage external monitoring assets',
        description: 'Users can add, modify and add context to assets that are being scanned',
      },
      ClientsExternalMonitoringView: {
        title: 'View external asset results',
        description: 'Users can view results from external monitoring asset scans',
      },
      ClientsEmergingThreatsView: {
        title: 'View Emerging Threats',
        description: 'User can view Emerging Threats.',
      },
      ClientsReviewSharingManage: {
        title: 'Manage review sharing',
        description: 'User can set the review sharing defaults for a whole community.',
      },
      ClientsReviewSharingSupplierManage: {
        title: 'Manage sharing for a supplier',
        description:
          'User can share a review of one supplier, and grant or deny requests for access to it.',
      },
      FederatedCustomPropertiesSettings: {
        title: 'Configure custom properties',
        description: 'User can create and edit custom properties.',
      },
      FederatedCustomPropertiesApply: {
        title: 'Apply custom properties',
        description: 'User can apply custom property values to sub-entities.',
      },
      FederatedLabelsApply: {
        title: 'Apply labels',
        description: 'User can apply labels to sub-entities.',
      },
      FederatedLabelsManage: {
        title: 'Manage labels',
        description: 'User can create and edit labels.',
      },
      FederatedNetworkView: {
        title: 'View network visualisations',
        description: 'User can view network visualisations.',
      },
      FederatedSubEntitiesView: {
        title: 'View sub-entities',
        description: 'User can view sub-entities.',
      },
      SettingsApiKeysManage: {
        title: 'Manage API keys',
        description: 'User can create and delete API keys.',
      },
      SettingsDiscussionSeen: {
        title: 'Change discussion seen setting',
        description: 'User can change the discussion seen setting.',
      },
      SettingsManageMode: {
        title: 'Enable platform modes',
        description: 'User can turn on different platform modes.',
      },
      SettingsOrgManage: {
        title: 'Edit organisation',
        description: 'User can edit organisation details.',
      },
      SettingsSsoManage: {
        title: 'Manage SSO settings',
        description: 'User can change SSO settings.',
      },
      SettingsUsersManage: {
        title: 'Manage users',
        description: 'User can create, edit and delete users.',
      },
      SettingsRolesManage: {
        title: 'Manage roles',
        description: 'User can create, edit and delete roles.',
      },
      SuppliersAssessmentEdit: {
        title: 'Answer assessment questions',
        description: 'User can answer assessment questions, change notes and attachments.',
      },
      SuppliersAssessmentSubmit: {
        title: 'Submit assessment',
        description: 'User can submit the assessment when complete.',
      },
      SuppliersClientsView: {
        title: 'View clients',
        description: 'User can view the clients list and profiles.',
      },
      SuppliersExternalMonitoringView: {
        title: 'View external monitoring results',
        description: 'Users can view results from external monitoring asset scans',
      },
      SuppliersExternalMonitoringManage: {
        title: 'Manage external monitoring assets',
        description: 'Users can add, modify and add context to assets that are being scanned',
      },
      SuppliersConnectionsManage: {
        title: 'Manage connection requests',
        description: 'User can create and respond to connection requests.',
      },
      SuppliersConnectionsRemove: {
        title: 'Remove clients',
        description: 'User can remove client connections.',
      },
      SuppliersMergeRequestsManage: {
        title: 'Manage merge requests',
        description: 'User can manage merge requests.',
      },
      SuppliersDiscussionsSend: {
        title: 'Respond to discussions',
        description: 'User can create and respond to discussions.',
      },
      SuppliersEmergingThreatsRespond: {
        title: 'Respond to Emerging Threats',
        description: 'User can respond to Emerging Threats.',
      },
      SuppliersEmergingThreatsView: {
        title: 'View Emerging Threats',
        description: 'User can view Emerging Threats.',
      },
      SuppliersFilesDownload: {
        title: 'Preview and download files',
        description: 'User can preview and download files.',
      },
      SuppliersOwnSupplierView: {
        title: 'View critical suppliers',
        description: 'User can view critical suppliers.',
      },
      SuppliersOwnSupplierEdit: {
        title: 'Manage critical suppliers',
        description: 'User can add and remove critical suppliers.',
      },
      SuppliersProfileSharingManage: {
        title: 'Manage Trust Centre links',
        description: 'User can create, edit and delete Trust Centre links.',
      },
      SuppliersProfileSharingView: {
        title: 'View Trust Centre links',
        description: 'User can view Trust Centre links.',
      },
      SuppliersRemediationsRespond: {
        title: 'Respond to remediations',
        description: 'User can respond to remediations.',
      },
      ZeusCommunitiesView: {
        title: 'View communities',
        description: 'User can view all communities on the platform.',
      },
      ZeusCommunitiesManage: {
        title: 'Manage communities',
        description: 'User can create, edit and delete communities.',
      },
      ZeusCommunitiesMembers: {
        title: 'Manage community members',
        description: 'User can add and remove members from communities.',
      },
    } satisfies Record<keyof typeof Permission, { title: string; description: string }>,
    deleteModal: {
      title: 'Delete role',
      description: 'Are you sure you want to delete the {roleName} role?',
      close: 'Close',
      delete: 'Delete',
      replacementRole: 'Replacement role',
      replacementDescription:
        'There are users assigned to this role which require a replacement role to be assigned to them.',
      replacementRoleEmpty: 'Please select a replacement role',
      error: 'Failed to delete role',
      replacementRoleRequired: 'Select a replacement role',
    },
  },
};
